import {BaseStage} from "./BaseStage";
import {initFlatPickerInput} from "../../../helpers/inputs/flatPicker/flatPicker";
import {toPriceRange} from "../../../helpers/formats/currency";

class DateSelectStage extends BaseStage {
    /** @var {Object<string,PublicEventResource.PublicEventResource[]>} */
    eventsByDate;
    flatpickrHtmlInitializedIntervalId;
    /** @var {HTMLElement} */
    templatesContainer;

    constructor(globalState) {
        super(globalState);

        // Init dom container
        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Retrieve template container
        this.templatesContainer = document.querySelector('.templates .date-select-stage-template');

        // Clone template
        const stageWrapper = this.templatesContainer.querySelector('.stage-wrapper').cloneNode(true);

        // Prepare pending event dates to place in calendar
        //// Group events by their date
        //// Each key is a HTML date and the contents an array of events for that day
        /** @var {Object<string,PublicEventResource.PublicEventResource[]>} */
        this.eventsByDate = {};

        this.globalState.parentEvent.pendingDates.forEach(event => {
            if (this.eventsByDate[event.htmlDate] === undefined) {
                this.eventsByDate[event.htmlDate] = [];
            }

            this.eventsByDate[event.htmlDate].push(event);
        })

        //// Get enabled dates
        const enabledDatesArray = Object.keys(this.eventsByDate);

        // Init flatpickr instance
        const calendarElement = stageWrapper.querySelector('.dummy-input');

        this.flatPickrInstance = initFlatPickerInput(calendarElement, {
            inline: true,
            enable: enabledDatesArray,
            // Because events come presorted, first and last
            // dates in the array are also the first and last shows
            minDate: enabledDatesArray[0],
            maxDate: enabledDatesArray[enabledDatesArray.length - 1],

            // Update HTML on change
            onChange: () => {
                this.initializeFlatpickrDateHtml();
                this.handleDateChange();
            },
            onMonthChange: () => this.initializeFlatpickrDateHtml(),
        })

        // Update HTML on a interval, as a fallback
        this.flatpickrHtmlInitializedIntervalId = setInterval(() => {
            this.initializeFlatpickrDateHtml();
        }, 16)

        // Store date info wrapper
        this.dateInfoWrapper = stageWrapper.querySelector('.show-info-container-wrapper');


        // Append stage to DOM
        this.domContainer.appendChild(stageWrapper);

        // Run base class initializations
        this.initSharedComponents();
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'date-select';
    }

    clearDom() {
        // Clear stage HTML
        this.domContainer.innerHTML = '';

        // Stop interval
        clearInterval(this.flatpickrHtmlInitializedIntervalId);
    }

    /**
     * Flatpickr does not let us easily change the days HTML content
     * So this takes care of doing it
     */
    initializeFlatpickrDateHtml() {
        // Check for all non initialized elements
        this.domContainer.querySelectorAll(".flatpickr-day:not(.initialized)")
            .forEach(calendarDayContainer => {
                const defaultContent = calendarDayContainer.innerHTML;

                let customContentString = '';

                // Create Date instance from aria label
                const dateInstance = new Date(calendarDayContainer.ariaLabel);

                const dayName = dateInstance.toLocaleDateString('en-US', {
                    weekday: 'long',
                }).toUpperCase();

                // Retrieve related event date
                const htmlDate = dateInstance.toISOString().split('T')[0];

                const relatedEvents = this.eventsByDate[htmlDate];

                let eventTimesString = '';

                if (relatedEvents) {
                    eventTimesString = relatedEvents.map(event => {
                        return `<div class="event-time">${event.timeOnly}</div>`;
                    }).join('\n');
                }

                // Retrieve custom content if exists
                const dateCustomContentElement = document.querySelector(`.pre-show-entertainment-calendar *[data-date="${calendarDayContainer.ariaLabel}"]`)

                // Enabled only for christmas event with id 126
                if (dateCustomContentElement && this.globalState.parentEvent.id === '126') {
                    customContentString = dateCustomContentElement.innerHTML;
                }

                // Update HTML
                calendarDayContainer.innerHTML = `
                    <div class="large-screen d-none d-md-block">
                         <div class="day-header">
                            <div class="day-number">
                                ${defaultContent}
                            </div>
                            <div
                                class="day-name small text-muted pl-2">
                                ${dayName}
                            </div>
                        </div>
                        <div class="date-details px-3">
                            <div class="times-container">
                                ${eventTimesString}
                            </div>
                            <div class="custom-content-container">
                                ${customContentString}
                            </div>
                        </div>
                    </div>
                    <div class="small-screen d-block d-md-none">
                        <div class="day-number">
                            ${defaultContent}
                        </div>
                    </div>
                `;

                calendarDayContainer.classList.add('initialized')
            })
    }

    handleDateChange() {
        // Retrieve related date shows
        const date = this.flatPickrInstance.input.value;
        const shows = this.eventsByDate[date];

        // Default to first available date
        this.globalState.setEventDate(shows[0]);

        // Disable stage advance
        this.disableNextStageButton();

        // Display event data
        this.renderDateInfoContainer();

        // Scroll to recently added container, after a bit
        setTimeout(() => {
            window.scrollTo({
                behavior: 'smooth',
                top: 1000000,
            })
        }, 16)
    }

    renderDateInfoContainer() {
        const state = this.globalState;
        const parentEvent = state.parentEvent;
        const parentEventPrice = parentEvent.priceData;

        // Clear existing HTML
        this.dateInfoWrapper.innerHTML = '';

        // Clone new container
        const container = this.templatesContainer.querySelector('.show-info-container').cloneNode(true);

        // Set event name & description
        container.querySelector('.show-name').innerHTML = parentEvent.title;
        container.querySelector('.show-description').innerHTML = parentEvent.description;


        // Setup general and reserved price data and availability
        const generalAdmissionCard = container.querySelector('.ticket-type-card.general-admission')
        const reservedCard = container.querySelector('.ticket-type-card.reserved')

        generalAdmissionCard.querySelector('.price-range').innerHTML = toPriceRange(
            parentEventPrice.generalAdmission.lowestPrice,
            parentEventPrice.generalAdmission.highestPrice,
        )

        reservedCard.querySelector('.price-range').innerHTML = toPriceRange(
            parentEventPrice.reservedSection.lowestPrice,
            parentEventPrice.reservedSection.highestPrice,
        )

        // Hide tickets if section is not available
        if (parentEventPrice.generalAdmission.isAvailable === false) {
            generalAdmissionCard.classList.add('d-none')
        }
        if (parentEventPrice.reservedSection.isAvailable === false) {
            reservedCard.classList.add('d-none')
        }

        // Hide if not available

        // Set selected date
        // TODO: Make a select to allow multiple shows in one day
        container.querySelector('.date-and-time-container').innerHTML =
            `<div>${state.selectedDate.longDateOnly} <br> ${state.selectedDate.timeOnly}</div>`;

        // Set custom content
        // Retrieve custom content if exists
        const dateCustomContentElement = document.querySelector(`.pre-show-entertainment-ticket-type-header *[data-date="${state.selectedDate.htmlDate}"]`)

        // Enabled only for christmas event with id 126
        if (dateCustomContentElement && state.parentEvent.id === '126') {
            container.querySelector('.custom-content').innerHTML = dateCustomContentElement.innerHTML;
        }

        // Setup ticket selection
        [generalAdmissionCard, reservedCard].forEach(ticketTypeCard => {
            const ticketInput = ticketTypeCard.querySelector(`input`);

            ticketInput.addEventListener('change', () => {
                this.handleSeatTypeSelect(ticketTypeCard);
            })

            // Trigger show selection
            ticketTypeCard.addEventListener('click', () => {
                ticketInput.checked = true;
                ticketInput.dispatchEvent(new InputEvent('change'));
            })
        })

        // Update seat map image
        const seatImageElement = container.querySelector('.seat-map-image');
        const mapContainer = seatImageElement.closest('.map-container');

        // Check if we have a seat map image
        if (state.parentEvent.seatMapImageRoute) {
            // Display seat map
            mapContainer.classList.remove('d-none');
            seatImageElement.src = state.parentEvent.seatMapImageRoute;
        } else {
            // Hide seat map
            mapContainer.classList.add('d-none');
            seatImageElement.src = '';
        }

        // Update banner image
        const homeImageElement = container.querySelector('.home-map-image');
        const homeContainer = homeImageElement.closest('.map-container');

        // Check if we have a seat map image
        if (state.parentEvent.homeImageRoute) {
            // Display seat map
            homeContainer.classList.remove('d-none');
            homeImageElement.src = state.parentEvent.homeImageRoute;
        } else {
            // Hide seat map
            homeContainer.classList.add('d-none');
            homeImageElement.src = '';
        }

        // Append to DOM
        this.dateInfoWrapper.appendChild(container);
    }

    /**
     * @param {HTMLElement} ticketTypeContainer
     */
    handleSeatTypeSelect(ticketTypeContainer) {
        /** @var {HTMLInputElement} */
        const ticketTypeInput = ticketTypeContainer.querySelector(`input`);

        // Clear selection from other tickets
        this.domContainer.querySelectorAll('.ticket-type-card').forEach(ticketCard => {
            ticketCard.classList.remove('selected');
            ticketCard.querySelector('input').checked = false;
        })

        // Mark show as selected
        ticketTypeInput.checked = true;
        // And add selected class
        ticketTypeContainer.classList.add('selected');

        // Save global state
        // noinspection JSCheckFunctionSignatures
        this.globalState.setSeatType(ticketTypeInput.value);

        this.enableNextStageButton();
    }
}

export {DateSelectStage};
